import { formatToolbarCount } from '@/components/core/ToolbarTable/toolbarLayout';
import { LOCAL_STORAGE } from '@/constants';

type Translate = (
    key: string,
    interpolationValues?: Record<string, string | number>,
) => string;

const currentLanguageKey = (): string | null => (
    typeof localStorage === 'undefined' ? null : localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY)
);

/** Thousands separators follow the app language, as in the toolbar counter. */
const formatEntryCount = (value: number): string => formatToolbarCount(value, currentLanguageKey());

type SelectAllLabelInput = {
    totalCount: number;
    overCountLimit?: boolean;
    countLimit: number;
};

/**
 * Select all label above the hit list. Only 1 takes the singular, 0 and
 * every other number take the plural. Past the count limit the label
 * offers the limit, because the exact total is not known yet.
 */
const selectAllLabel = (
    t: Translate,
    { totalCount, overCountLimit = false, countLimit }: SelectAllLabelInput,
): string => {
    if (overCountLimit) {
        return t('ITEM.SELECT_OVER_ENTRIES', { count: formatEntryCount(countLimit) });
    }
    if (totalCount === 1) {
        return t('ITEM.SELECT_ENTRY', { count: formatEntryCount(1) });
    }
    return t('ITEM.SELECT_ALL_ENTRIES', { count: formatEntryCount(totalCount) });
};

type ShowingLabelInput = {
    visibleRowCount: number;
    numberFound: number;
    maxNumberFound: number;
    hasCursor?: boolean;
};

type ShowingLabel = {
    text: string;
    /** The total is a lower bound, so the exact count can be recalculated. */
    approximate: boolean;
};

/**
 * "Showing … entries" label above the hit list. Keeps the branches of
 * `search-filter.tpl.html`: a total past the limit, or more loaded rows
 * than the backend counted while a cursor remains, read "loaded from over".
 */
const showingEntriesLabel = (
    t: Translate,
    { visibleRowCount, numberFound, maxNumberFound, hasCursor = false }: ShowingLabelInput,
): ShowingLabel | null => {
    if (visibleRowCount <= 0) {
        return null;
    }

    const overLimit = numberFound > maxNumberFound;
    if (overLimit || (numberFound < visibleRowCount && hasCursor)) {
        return {
            text: t('ITEM.SHOWING_ENTRIES_LOADED_FROM_OVER', {
                from: formatEntryCount(1),
                to: formatEntryCount(visibleRowCount),
                count: formatEntryCount(overLimit ? maxNumberFound : visibleRowCount),
            }),
            approximate: true,
        };
    }

    const total = Math.max(numberFound, visibleRowCount);
    if (total === 1) {
        return {
            text: t('ITEM.SHOWING_ENTRY', { to: formatEntryCount(1), count: formatEntryCount(1) }),
            approximate: false,
        };
    }
    return {
        text: t('ITEM.SHOWING_ENTRIES', {
            from: formatEntryCount(1),
            to: formatEntryCount(visibleRowCount),
            count: formatEntryCount(total),
        }),
        approximate: false,
    };
};

export type { SelectAllLabelInput, ShowingLabel, ShowingLabelInput };
export { formatEntryCount, selectAllLabel, showingEntriesLabel };
